import { useState, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, User, Lock, Loader2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import { useIsAgencyOwner } from '../../hooks/useIsAgencyOwner';
import { useOnboardingProgress } from '../../hooks/useOnboardingProgress';
// V4-ZERNIO-INBOX-BADGE-MOBILE 2026-05-29 — parité desktop : badge unread count sur Engagement
import { useUnrepliedCount } from '../../hooks/useUnrepliedCount';
import { WatchTab } from '../features/tabs/WatchTab';
import { PositioningTab } from '../features/tabs/PositioningTab';
import { AutomationsTab } from '../features/tabs/AutomationsTab';
import { SettingsTab } from '../features/tabs/SettingsTab';
import { AccountTab } from '../features/tabs/AccountTab';
import { AnalyticsTab } from '../features/tabs/AnalyticsTab';
import { EngagementTab } from '../features/tabs/EngagementTab';
// V4-CODESPLIT-DASHBOARD 2026-05-29 — lazy-load heavy tabs
const SpacesTab = lazy(() => import('../features/tabs/SpacesTab').then(m => ({ default: m.SpacesTab })));
const SourcesTab = lazy(() => import('../features/tabs/SourcesTab').then(m => ({ default: m.SourcesTab })));
const ChannelsTab = lazy(() => import('../features/tabs/ChannelsTab').then(m => ({ default: m.ChannelsTab })));
const NewsletterTab = lazy(() => import('../features/tabs/NewsletterTab').then(m => ({ default: m.NewsletterTab })));

const TabSuspenseFallback = () => (
  <div className="flex items-center justify-center p-8">
    <Loader2 className="w-5 h-5 animate-spin" />
  </div>
);
import { getMenuItems, getSettingsItem } from '../../config/menuItems';
import { SIDEBAR_ITEMS } from '../../utils/Constants';
import { filterNavItems, shouldShowSettingsItem } from '../../utils/navGating';
import type { Source, WatchTopic, CommunicationChannel, AutomationSettings, User as UserType, Space } from '../../types';
import type { Post } from '../../types/post';

interface MobileBurgerMenuProps {
  sources: Source[];
  onAddSource: (source: Omit<Source, 'id'>) => void;
  onUpdateSource: (id: string, source: Omit<Source, 'id'>) => void;
  onDeleteSource: (id: string) => void;
  onRefreshSources?: () => Promise<void>;
  watchTopics: WatchTopic[];
  enrichingTopicIds?: Set<string>;
  onAddTopic: (topic: Omit<WatchTopic, 'id'>) => void;
  onUpdateTopic: (id: string, topic: Omit<WatchTopic, 'id'>) => void;
  onDeleteTopic: (id: string) => void;
  communicationChannels: CommunicationChannel[];
  onAddCommunicationChannel: (channel: Omit<CommunicationChannel, 'id'>) => void;
  onUpdateCommunicationChannel: (id: string, channel: Omit<CommunicationChannel, 'id'>) => void;
  onDeleteCommunicationChannel: (id: string) => void;
  onRefreshChannels?: () => Promise<void>;
  automationSettings: AutomationSettings;
  onUpdateAutomationSettings: (settings: AutomationSettings) => void;
  systemPrompt: string;
  // V4-MOBILE-MENU-POSITIONING-TEMPLATE 2026-05-29 — parité desktop : le hook
  // useOnboardingProgress a besoin de savoir si le prompt est encore un
  // template par défaut. Sans cette info, POSITIONING peut être marqué
  // "complete" dès qu'un template default est en place (faux positif).
  isPositioningTemplate?: boolean;
  // V4-ONBOARDING-CHECKLIST-OPTIONAL — parité desktop : topics/sources optionnels
  // si la veille sectorielle est désactivée (personal branding).
  sectorWatchEnabled?: boolean;
  onUpdateSystemPrompt: (prompt: string) => void;
  newsletterTemplate: string;
  onUpdateNewsletterTemplate: (template: string) => void;
  queuePosts: Post[];
  allPosts?: Post[];
  logoUrl?: string;
  onUpdateLogoUrl?: (url: string) => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  user: UserType | null;
  currentSpace: Space | null;
  activeTab?: string | null;
  onTabChange?: (tabId: string | null) => void;
}

export function MobileBurgerMenu({
  sources,
  onAddSource,
  onUpdateSource,
  onDeleteSource,
  onRefreshSources,
  watchTopics,
  enrichingTopicIds,
  onAddTopic,
  onUpdateTopic,
  onDeleteTopic,
  communicationChannels,
  onAddCommunicationChannel,
  onUpdateCommunicationChannel,
  onDeleteCommunicationChannel,
  onRefreshChannels,
  automationSettings,
  onUpdateAutomationSettings,
  systemPrompt,
  isPositioningTemplate,
  sectorWatchEnabled,
  onUpdateSystemPrompt,
  newsletterTemplate,
  onUpdateNewsletterTemplate,
  queuePosts,
  allPosts,
  logoUrl,
  onUpdateLogoUrl,
  isOpen,
  onOpenChange,
  user,
  currentSpace,
  activeTab,
  onTabChange
}: MobileBurgerMenuProps) {
  const [internalSelectedItem, setInternalSelectedItem] = useState<string | null>(null);
  const selectedItem = activeTab !== undefined ? activeTab : internalSelectedItem;
  const { t } = useLanguage();

  const handleItemClick = (itemId: string) => {
    if (onTabChange) {
      onTabChange(itemId);
    } else {
      setInternalSelectedItem(itemId);
    }
  };

  const handleClose = () => {
    if (onTabChange) {
      onTabChange(null);
    } else {
      setInternalSelectedItem(null);
    }
    if (onOpenChange) {
      onOpenChange(false);
    }
  };

  // Handle back from panel - go back to menu instead of closing completely
  const handleBackToMenu = () => {
    if (onTabChange) {
      onTabChange(null);
    } else {
      setInternalSelectedItem(null);
    }
    // Don't call onOpenChange(false) - keep the menu open
  };
  
  const { isReadOnly, isGuest } = useAuth();
  const isAgencyOwner = useIsAgencyOwner();
  // V4-MOBILE-MENU-PARITY 2026-05-29 — réutilise la config sidebar desktop
  // pour avoir EXACTEMENT les mêmes items (ids = SIDEBAR_ITEMS) + lock state
  // partagé via useOnboardingProgress. Avant : drawer mobile maintenait sa
  // propre liste avec ids ad-hoc (`veille`, `canaux`) qui causait des labels
  // divergents (FINDING-MOBILE-03) et l'absence d'Analytics/Engagement
  // (FINDING-MOBILE-02).
  const onboarding = useOnboardingProgress({
    systemPrompt: systemPrompt || '',
    // V4-MOBILE-MENU-POSITIONING-TEMPLATE 2026-05-29 — parité desktop.
    isPositioningTemplate,
    watchTopics,
    sources,
    communicationChannels,
    posts: allPosts,
    // V4-ONBOARDING-CHECKLIST-OPTIONAL — topics/sources optionnels si veille off.
    sectorWatchEnabled,
  });

  // V4-ZERNIO-INBOX-BADGE-MOBILE 2026-05-29 — poll unreplied count toutes les 60s
  // pour parité avec Sidebar desktop (cf Sidebar.tsx). Le hook gère spaceId=null.
  const unrepliedCount = useUnrepliedCount(currentSpace?.id ?? null);

  // Check if translations are loaded
  if (!t || !t.menu || !t.settings) {
    return null;
  }

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  const allMenuItems = getMenuItems(t);
  const settingsItem = getSettingsItem(t);

  // Inclut SPACES uniquement si agency owner. Retire AUTOMATIONS pour readers.
  // V4-FRONT-MOBILE-MENU-PARITY-AUDIT — parité avec le desktop (Sidebar.tsx) :
  // tant que le positionnement n'est pas rempli, MASQUER (≠ griser) les items qui en dépendent.
  const POSITIONING_GATED_ITEMS: string[] = [
    SIDEBAR_ITEMS.WATCH, SIDEBAR_ITEMS.SOURCES, SIDEBAR_ITEMS.CHANNELS,
    SIDEBAR_ITEMS.ANALYTICS, SIDEBAR_ITEMS.ENGAGEMENT, SIDEBAR_ITEMS.NEWSLETTER,
  ];
  // Filtrage centralisé (parité stricte avec Sidebar desktop, cf navGating.ts).
  // Le guest ne voit que Engagement ; le burger affiche Espaces si owner d'agence.
  const menuItems = [
    ...filterNavItems(allMenuItems, {
      isGuest,
      isReadOnly,
      isAgencyOwner,
      topicsUnlocked: onboarding.unlocked.topics,
      onboardingReadOnly: onboarding.isReadOnly,
      positioningGatedItems: POSITIONING_GATED_ITEMS,
      showSpacesWhenOwner: true,
    }),
    ...(shouldShowSettingsItem(isGuest) ? [settingsItem] : []),
  ];

  // Calcule l'état lock de chaque item — même logique que Sidebar.tsx
  // (coming-soon pour AUTOMATIONS + NEWSLETTER, onboarding séquentiel sinon).
  const getItemLockState = (itemId: string): { disabled: boolean; tooltip?: string } => {
    if (itemId === SIDEBAR_ITEMS.AUTOMATIONS || itemId === SIDEBAR_ITEMS.NEWSLETTER) {
      return { disabled: true, tooltip: t.onboarding.comingSoonTooltip };
    }
    if (onboarding.isReadOnly) return { disabled: false };
    const onb = t.onboarding;
    if (itemId === SIDEBAR_ITEMS.WATCH && !onboarding.unlocked.topics) {
      return { disabled: true, tooltip: onb.lockedTopicsTooltip };
    }
    if (itemId === SIDEBAR_ITEMS.SOURCES && !onboarding.unlocked.sources) {
      return { disabled: true, tooltip: onb.lockedSourcesTooltip };
    }
    if (itemId === SIDEBAR_ITEMS.CHANNELS && !onboarding.unlocked.channels) {
      return { disabled: true, tooltip: onb.lockedChannelsTooltip };
    }
    if (itemId === SIDEBAR_ITEMS.ANALYTICS && !onboarding.unlocked.analytics) {
      return { disabled: true, tooltip: onb.lockedAnalyticsTooltip };
    }
    if (itemId === SIDEBAR_ITEMS.ENGAGEMENT && !onboarding.unlocked.engagement) {
      return { disabled: true, tooltip: onb.lockedEngagementTooltip };
    }
    return { disabled: false };
  };

  const renderContent = () => {
    switch (selectedItem) {
      case SIDEBAR_ITEMS.SPACES:
        return (
          <Suspense fallback={<TabSuspenseFallback />}>
            <SpacesTab onClose={handleBackToMenu} />
          </Suspense>
        );
      case SIDEBAR_ITEMS.SOURCES:
        return (
          <Suspense fallback={<TabSuspenseFallback />}>
            <SourcesTab
              sources={sources}
              onAddSource={onAddSource}
              onUpdateSource={onUpdateSource}
              onDeleteSource={onDeleteSource}
              onRefreshSources={onRefreshSources}
              onClose={handleBackToMenu}
              watchTopics={watchTopics}
            />
          </Suspense>
        );
      case SIDEBAR_ITEMS.WATCH:
        return (
          <WatchTab
            watchTopics={watchTopics}
            enrichingTopicIds={enrichingTopicIds}
            onAddTopic={onAddTopic}
            onUpdateTopic={onUpdateTopic}
            onDeleteTopic={onDeleteTopic}
            onClose={handleBackToMenu}
          />
        );
      case SIDEBAR_ITEMS.POSITIONING:
        return (
          <PositioningTab
            systemPrompt={systemPrompt}
            onUpdatePrompt={onUpdateSystemPrompt}
            onClose={handleBackToMenu}
          />
        );
      case SIDEBAR_ITEMS.CHANNELS:
        return (
          <Suspense fallback={<TabSuspenseFallback />}>
            <ChannelsTab
              channels={communicationChannels}
              onAddChannel={onAddCommunicationChannel}
              onUpdateChannel={onUpdateCommunicationChannel}
              onDeleteChannel={onDeleteCommunicationChannel}
              onRefreshChannels={onRefreshChannels}
              onClose={handleBackToMenu}
              queuePosts={queuePosts}
              allPosts={allPosts}
            />
          </Suspense>
        );
      case SIDEBAR_ITEMS.ANALYTICS:
        return <AnalyticsTab onClose={handleBackToMenu} />;
      case SIDEBAR_ITEMS.ENGAGEMENT:
        return <EngagementTab onClose={handleBackToMenu} />;
      case SIDEBAR_ITEMS.NEWSLETTER:
        return (
          <Suspense fallback={<TabSuspenseFallback />}>
            <NewsletterTab
              template={newsletterTemplate}
              onUpdateTemplate={onUpdateNewsletterTemplate}
              onClose={handleBackToMenu}
              queuePosts={queuePosts}
              communicationChannels={communicationChannels}
            />
          </Suspense>
        );
      case SIDEBAR_ITEMS.AUTOMATIONS:
        return (
          <AutomationsTab
            automationSettings={automationSettings}
            communicationChannels={communicationChannels}
            onUpdateSettings={onUpdateAutomationSettings}
            onClose={handleBackToMenu}
          />
        );
      case SIDEBAR_ITEMS.SETTINGS:
        return (
          <SettingsTab
            onClose={handleBackToMenu}
          />
        );
      case SIDEBAR_ITEMS.ACCOUNT:
        return (
          <AccountTab
            onClose={handleBackToMenu}
          />
        );
      default:
        return null;
    }
  };

  // Use portal to render at document.body level
  const menuContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 backdrop-blur-sm"
            style={{ 
              zIndex: 20001,
              backgroundColor: 'rgba(0, 0, 0, 0.3)'
            }}
            onClick={handleClose}
          />

          {/* Menu panel - full width */}
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="fixed left-0 top-0 h-full w-full overflow-y-auto"
            style={{ 
              zIndex: 20002,
              backgroundColor: 'var(--color-background)',
            }}
          >
            {selectedItem ? (
              // Panel content
              renderContent()
            ) : (
              // Menu list
              <div className="h-full flex flex-col">
                {/* Header avec logo et bouton fermer */}
                <div 
                  className="px-6 py-6 flex items-center justify-between flex-shrink-0"
                  style={{ borderBottom: `1px solid var(--color-border)` }}
                >
                  {logoUrl && (
                    <img src={logoUrl} alt="Logo" className="h-10 object-contain" />
                  )}
                  <button
                    onClick={handleClose}
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'var(--color-surface)' }}
                    aria-label="Fermer le menu"
                  >
                    <X className="w-5 h-5" style={{ color: 'var(--color-text-primary)' }} />
                  </button>
                </div>

                {/* Menu items */}
                <div className="px-4 py-6 flex-1">
                  {menuItems.map((item) => {
                    const Icon = item.icon;
                    const lock = getItemLockState(item.id);
                    const isDisabled = lock.disabled;
                    const isActive = selectedItem === item.id;
                    // V4-ZERNIO-INBOX-BADGE-MOBILE 2026-05-29 — badge count seulement sur Engagement
                    const badgeCount = item.id === SIDEBAR_ITEMS.ENGAGEMENT ? unrepliedCount : 0;
                    return (
                      <button
                        key={item.id}
                        onClick={() => !isDisabled && handleItemClick(item.id)}
                        disabled={isDisabled}
                        // V4-MOBILE-MENU-A11Y 2026-05-29 — aria-label + aria-current
                        // pour les lecteurs d'écran (FINDING-MOBILE-05).
                        aria-label={lock.tooltip ? `${item.label} — ${lock.tooltip}` : item.label}
                        aria-current={isActive ? 'page' : undefined}
                        aria-disabled={isDisabled}
                        title={lock.tooltip || item.label}
                        className="w-full flex items-center gap-4 px-4 py-4 rounded-[20px] transition-all"
                        style={{
                          color: isDisabled ? 'var(--color-text-secondary)' : 'var(--color-text-primary)',
                          marginBottom: '8px',
                          opacity: isDisabled ? 0.5 : 1,
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                        }}
                        onMouseEnter={(e) => {
                          if (!isDisabled) {
                            e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                            e.currentTarget.style.transform = 'translateX(4px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isDisabled) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.transform = 'translateX(0)';
                          }
                        }}
                      >
                        <div
                          className="w-11 h-11 rounded-full flex items-center justify-center relative"
                          style={{ backgroundColor: isDisabled ? 'var(--color-surface-hover)' : 'var(--color-primary)' }}
                        >
                          <Icon className="w-5 h-5" style={{ color: isDisabled ? 'var(--color-text-secondary)' : 'var(--color-text-light)' }} />
                          {/* Badge cadenas — visible quand item locké (mirror du
                              pattern Sidebar desktop, indispensable sur mobile
                              où il n'y a pas de hover). */}
                          {isDisabled && (
                            <span
                              className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center rounded-full"
                              style={{
                                width: 16,
                                height: 16,
                                backgroundColor: 'var(--color-warning-light, rgba(245, 158, 11, 0.15))',
                                border: '1px solid var(--color-warning)',
                              }}
                              aria-hidden="true"
                            >
                              <Lock style={{ width: 9, height: 9, color: 'var(--color-warning)' }} />
                            </span>
                          )}
                          {/* V4-ZERNIO-INBOX-BADGE-MOBILE 2026-05-29 — pastille count, mirror Sidebar desktop */}
                          {!isDisabled && badgeCount > 0 && (
                            <span
                              className="absolute -top-1 -right-1 flex items-center justify-center rounded-full px-1 font-semibold"
                              style={{
                                minWidth: 16,
                                height: 16,
                                fontSize: 10,
                                lineHeight: 1,
                                backgroundColor: 'var(--color-danger, #ef4444)',
                                color: '#fff',
                                border: '1.5px solid var(--color-background)',
                              }}
                              aria-label={`${badgeCount} non lu${badgeCount > 1 ? 's' : ''}`}
                            >
                              {badgeCount > 99 ? '99+' : badgeCount}
                            </span>
                          )}
                        </div>
                        <span className="font-medium">{item.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* User Profile — click to open account */}
                {user && (
                  <button
                    onClick={() => handleItemClick(SIDEBAR_ITEMS.ACCOUNT)}
                    aria-label={t.quota.accountTitle}
                    className="w-full pl-10 pr-8 py-6 mt-auto flex items-center gap-4 cursor-pointer transition-colors"
                    style={{ borderTop: `1px solid var(--color-border)` }}
                  >
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden border flex-shrink-0"
                      style={{ borderColor: 'var(--color-border)' }}
                    >
                      {user.avatarUrl ? (
                        <img src={user.avatarUrl} alt={user.email} className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-6 h-6" style={{ color: 'var(--color-text-secondary)' }} />
                      )}
                    </div>
                    <div className="flex flex-col text-left">
                      <span className="font-medium text-sm" style={{ color: 'var(--color-text-primary)' }}>
                        {user.email}
                      </span>
                      {currentSpace && (
                        <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                          {currentSpace.name}
                        </span>
                      )}
                    </div>
                  </button>
                )}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  // Render in portal
  return createPortal(menuContent, document.body);
}